import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import CardWidget from "../card-widget/CardWidget";
import CardWidgetContent from "../card-widget/CardWidgetContent";
import CardWidgetErrorsContainer from "../card-widget/CardWidgetErrorsContainer";
import Api from "../../services/api/Api";
import { ApiPaths } from "../../services/api/ApiPaths";
import { RouteConstants } from "../route/services/RouteConstants";
import { PASSWORD_RESET_STATUS } from "../password-reset/PasswordResetStatus";
import { getLanguage, getSecurityQuestions, isEnglishActive } from "../../store/rootReducer";

import SecurityQuestionAnswer from "../registration/fields/SecurityQuestionAnswer";
import _find from "lodash/find";
import UrlParamsUtil from "../../services/util/UrlParamsUtil";

const saveStateToLocalStorage = (token, state) => {
    const stateToSave = {
        errorMessage: state.errorMessage,
        passwordResetToken: state.passwordResetToken,
        securityQuestionId: state.securityQuestionId,
        securityQuestionHint: state.securityQuestionHint,
        securityQuestionAnswer: state.securityQuestionAnswer,
        attemptsLeft: state.attemptsLeft,
        isLocked: state.isLocked,
        lockExpiresAt: state.lockExpiresAt,
    };
    // Save state with the token as part of the key
    localStorage.setItem(`passwordResetState_${token}`, JSON.stringify(stateToSave));
};

const loadStateFromLocalStorage = (token) => {
    // Load state specific to the user's token
    const state = localStorage.getItem(`passwordResetState_${token}`);
    return state ? JSON.parse(state) : null;
};

class PasswordResetAuthForm extends Component {

    constructor(props) {
        super(props);
        const parsedParams = UrlParamsUtil.getQueryParamsFromUrl(this.props);
        const passwordResetToken = parsedParams.token || null;
        const savedState = passwordResetToken ? loadStateFromLocalStorage(passwordResetToken) : null;

        this.state = savedState || {
            errorMessage: null,
            passwordResetToken: passwordResetToken,
            securityQuestionId: null,
            securityQuestionHint: null,
            securityQuestionAnswer: null,
            attemptsLeft: 4,
            isLocked: false,
            lockExpiresAt: null
        };
    }

    submitSecurity = () => {
        const { passwordResetToken, isLocked, lockExpiresAt } = this.state;
        const now = new Date();
        if (isLocked && lockExpiresAt && now < new Date(lockExpiresAt)) {
            this.setState({
                errorMessage: "password_reset_auth.password_reset_auth_failed_with_exceeded_login_attempts",
            });
            return;
        }

        let request = {
            passwordResetToken: passwordResetToken,
            securityQuestionId: this.state.securityQuestionId,
            securityQuestionAnswer: this.state.securityQuestionAnswer
        };

        Api.post(ApiPaths.PASSWORD_RESET_AUTH, request).then(
            (response) => {
                if (response.data.status === PASSWORD_RESET_STATUS.AUTH_SUCCESS) {
                    this.props.onAuthSuccess(passwordResetToken);
                } else if (response.data.status === PASSWORD_RESET_STATUS.INVALID_TOKEN) {
                    this.props.history.push(RouteConstants.PASSWORD_RESET_ERROR);
                } else if (response.data.status === PASSWORD_RESET_STATUS.AUTH_FAILED) {
                    let securityQuestionHint = response.data.securityQuestionHint;
                    let attemptsLeft = this.state.attemptsLeft - 1;
                    if (attemptsLeft <= 0) {
                        const lockExpiresAt = new Date(new Date().getTime() + 15 * 60 * 1000);  // Lock for 15 minutes
                        this.setState({
                            errorMessage: "password_reset_auth.password_reset_auth_failed_with_exceeded_login_attempts",
                            isLocked: true,
                            lockExpiresAt: lockExpiresAt,
                            attemptsLeft: 0,
                        }, () => saveStateToLocalStorage(passwordResetToken, this.state));
                    } else if (attemptsLeft >= 0 && securityQuestionHint) {
                        this.setState({
                            errorMessage: "password_reset_auth.password_reset_auth_failed_with_hint",
                            securityQuestionHint: securityQuestionHint,
                            attemptsLeft: attemptsLeft
                        }, () => saveStateToLocalStorage(passwordResetToken, this.state));
                    } else {
                        this.setState({
                            errorMessage: "password_reset_auth.password_reset_auth_failed"
                        });
                    }

                } else {
                    this.setState({
                        errorMessage: "common.general_processing_error"
                    });
                }
            },
            (error) => {
                this.setState({
                    errorMessage: "common.general_processing_error"
                });
            }
        );
    };


    onSecurityQuestionAnswerChanged = (e) => {
        let securityQuestionAnswer = e.target.value;
        this.setState({
            securityQuestionAnswer: securityQuestionAnswer
        });
    };


    checkLockStatus = () => {
        const { passwordResetToken, isLocked, lockExpiresAt } = this.state;
        const now = new Date();
        if (isLocked && lockExpiresAt && now >= new Date(lockExpiresAt)) {
            this.setState({
                isLocked: false,
                lockExpiresAt: null,
                attemptsLeft: 4,
                errorMessage: null,
            }, () => saveStateToLocalStorage(passwordResetToken, this.state));
        }
    };


    handleSubmit = (e) => {
        e.preventDefault();
        if (!this.state.securityQuestionAnswer) {
            this.submitSecurity();
        }
    };

    getCardContent = () => {
        return (
            <form onSubmit={(e) => this.handleSubmit(e)}>
                <CardWidgetContent contentText="password_reset_auth.password_reset_auth_desc" />
                <CardWidgetErrorsContainer errorMessage={this.state.errorMessage} errorMessageParams={{ "securityQuestionHint": this.state.securityQuestionHint, "attemptsLeft": this.state.attemptsLeft }} />
                <SecurityQuestionAnswer
                    onChange={this.onSecurityQuestionAnswerChanged}
                />
            </form>
        )
    };

    componentDidMount() {
        let parsedParams = UrlParamsUtil.getQueryParamsFromUrl(this.props);
        if (parsedParams.token) {
            this.setState({
                passwordResetToken: parsedParams.token
            })
        }

        if (parsedParams.qId) {
            this.setState({
                securityQuestionId: parseInt(parsedParams.qId, 10)
            })
        }

        this.checkLockStatus();
    }

    componentDidUpdate(prevProps, prevState) {
        const { passwordResetToken } = this.state;
        if (prevState !== this.state && passwordResetToken) {
            saveStateToLocalStorage(passwordResetToken, this.state);
        }
    }

    render() {
        return (
            <CardWidget getCardContent={this.getCardContent}
                isSubmitDisabled={!this.state.securityQuestionId || !this.state.securityQuestionAnswer}
                submitButtonAction={() => this.submitSecurity()}
                submitButtonText="forgotpassword.next" />
        )
    };
}

const mapStateToProps = (state) => {
    return {
        language: getLanguage(state),
        isEnglishActive: isEnglishActive(state),
        securityQuestions: getSecurityQuestions(state)
    };
};

PasswordResetAuthForm.propTypes = {
    onAuthSuccess: PropTypes.func,
    passwordResetToken: PropTypes.string,
    securityQuestionId: PropTypes.string
};

export default connect(mapStateToProps)(PasswordResetAuthForm);